import React from "react";
import PropTypes from "prop-types";
import { humanEUR, pct } from "./ownershipFormat";

// Soft, calm palette (matching the Forecast "where it goes" bar) — owners in a soft cool sequence,
// the last tone reserved for a grouped "other owners" segment so the colour count never grows.
const SEG_TONES = ["bg-chart-teal", "bg-chart-2", "bg-chart-3", "bg-chart-pred"];
// Unowned is the gap the page surfaces — a soft rose (flat, no stripe) reads as the gap without
// shouting like a saturated danger fill.
const UNOWNED_TONE = "bg-[hsl(347_74%_82%)]";

function LegendItem({ swatch, name, value }) {
  return (
    <div className="flex items-center gap-1.5">
      {/* swatch size is inline: this project's tailwind overrides theme.height/width, so the sizing utilities are no-ops */}
      <span className={`inline-block shrink-0 rounded-sm ${swatch}`} style={{ height: 10, width: 10 }} />
      <span className="text-xs text-text-2">{name}</span>
      <span className="text-xs tabular-nums text-text-3">{value}</span>
    </div>
  );
}
LegendItem.propTypes = { swatch: PropTypes.string.isRequired, name: PropTypes.string.isRequired, value: PropTypes.string.isRequired };

// OwnershipCompositionBar answers the page's core question — "who owns this spend, and how much
// isn't owned at all?" — as one horizontal band sized by share. It is the dashboard's signature
// element and the thing that lets the page fill its full width with meaning (a sparse owner table
// alone leaves the width hollow). Reads well at one owner (one segment + the unowned remainder) and
// at many (top owners by colour, the long tail grouped, unowned flagged with the stripe pattern).
const OwnershipCompositionBar = ({ owners = [], grandTotal = 0, unassignedCost = 0 }) => {
  if (!(grandTotal > 0)) return null;
  const reserve = SEG_TONES.length - 1; // top owners get a distinct colour; the rest collapse to one
  const ranked = [...owners].sort((a, b) => (Number(b.cost) || 0) - (Number(a.cost) || 0));
  const head = ranked.slice(0, reserve);
  const tail = ranked.slice(reserve);
  const tailCost = tail.reduce((s, o) => s + (Number(o.cost) || 0), 0);

  const segs = head.map((o, i) => ({ key: `o${i}`, name: o.ownerName, cost: Number(o.cost) || 0, tone: SEG_TONES[i] }));
  if (tailCost > 0) segs.push({ key: "other", name: `${tail.length} other owner${tail.length === 1 ? "" : "s"}`, cost: tailCost, tone: SEG_TONES[reserve] });
  const unownedCost = Math.round(unassignedCost) >= 1 ? Number(unassignedCost) : 0;

  return (
    <div className="rounded-card border border-card-bd bg-card px-5 py-4">
      <div className="mb-3 text-xs font-medium uppercase tracking-wide text-text-2">How spend is owned</div>
      {/* height/width are inline: this project's tailwind overrides theme.height/width, so the sizing utilities no-op */}
      <div className="flex overflow-hidden rounded-full bg-surface-2" style={{ height: 20, width: "100%" }} role="img" aria-label="Spend ownership composition">
        {segs.map((s) => (
          <div
            key={s.key}
            className={s.tone}
            style={{ width: `${(s.cost / grandTotal) * 100}%`, height: "100%" }}
            title={`${s.name} · ${humanEUR(s.cost)} (${pct(s.cost / grandTotal)})`}
          />
        ))}
        {unownedCost > 0 && (
          <div
            className={UNOWNED_TONE}
            style={{ width: `${(unownedCost / grandTotal) * 100}%`, height: "100%" }}
            title={`Unowned · ${humanEUR(unownedCost)} (${pct(unownedCost / grandTotal)})`}
          />
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segs.map((s) => (
          <LegendItem key={s.key} swatch={s.tone} name={s.name} value={`${humanEUR(s.cost)} · ${pct(s.cost / grandTotal)}`} />
        ))}
        {unownedCost > 0 && (
          <LegendItem swatch={UNOWNED_TONE} name="Unowned" value={`${humanEUR(unownedCost)} · ${pct(unownedCost / grandTotal)}`} />
        )}
      </div>
    </div>
  );
};

OwnershipCompositionBar.propTypes = {
  owners: PropTypes.array,
  grandTotal: PropTypes.number,
  unassignedCost: PropTypes.number,
};

export default OwnershipCompositionBar;
